import type { CommandVersion as LegalentityCommandVersion } from '@festina/legalentity-general-types/legalentity'
import type { CommandVersion as LifeCommandVersion } from '@festina/life-general-types/life'
import type { CommandVersion as MoneyinCommandVersion } from '@festina/moneyin-general-types/moneyin'
import type { CommandVersion as UnitlinkCommandVersion } from '@festina/unitlink-general-types/unitlink'
import type {
  ExcludeString,
  Optional,
  Server,
  WeakBranded,
} from '@festina-life/core'
import type { CommandActionVariant } from '@festina-life/ui/theme'
import type { ComponentType, FC } from 'react'

import type { EventVersionWithCommandType } from '../../hooks/useFilterCommands'
import type { ActionCategories } from '../search/commandPalette/types'

export type AnyCommandVersion =
  | LifeCommandVersion
  | UnitlinkCommandVersion
  | MoneyinCommandVersion
  | LegalentityCommandVersion

export type ActionButtonProps = WeakBranded<
  {
    commandActionVariant: CommandActionVariant
    commandId: Optional<string>
    isAllowedToDoCalculation?: boolean
    eventVersionWithCommand: Optional<
      EventVersionWithCommandType<AnyCommandVersion>
    >
    eventId?: Optional<string>
    festinaComponent: Server
    /** Whether the client-side validation can be disabled by the user. */
    isValidationEnforced: boolean
    /** Some commands may exist in both an unapplied and applied view, therefor this extra boolean is required */
    isFutureCommand?: boolean
    extraRegistrationKey?: string
    /** `inline` renders the command content directly in the page instead of behind a button and modal. Only meant for the `read` variant, as it has no submit actions. */
    displayMode?: 'modal' | 'inline'
  },
  'GeneralActionProps'
>

export type CommitmentActionButtonProps = WeakBranded<
  ActionButtonProps,
  'CommitmentActionProps'
>

/**
 * Hook that generates action warnings for a command.
 * Stored in command metadata to centralize warning logic away from individual Command components.
 * The CommandModalButton automatically calls this hook for the 'create' variant.
 */
export type CommandWarningsHook = (
  // The props here should always be tied to the entityId.
  // As the command the warnings are decoupled we cannot type this here
  props?: unknown,
) => ActionData['status']['warnings']

export type CollectionButtons<T extends AnyCommandVersion> = Record<
  NonNullable<T>['_type'],
  ComponentType<ActionButtonProps>
>

type ActionWarningCode =
  | 'missingField'
  | 'hasField'
  | 'hasActivePartner'
  | 'noActivePartner'
  | 'noPartner'
  | 'expectedCommitmentStatus'
  | 'unexpectedCommitmentStatus'
  | 'unexpectedPolicyStatus'
  | 'unexpectedInvoiceStatus'
  | 'expectedDateAfter'
  | 'alreadyApplied'
  | 'aboveEligibleAge'
  | 'insufficientAccess'

export type CodedActionWarning = {
  code: ActionWarningCode
  payload?: string
}

type ActionAvailability = { warnings?: CodedActionWarning[] } & (
  | {
      isAvailable: true
    }
  | {
      isAvailable: false
      reasons: {
        code: ActionWarningCode
        payload?: string
      }[]
    }
)

export type ActionData = {
  key: string
  translation: string
  categories: ActionCategories
  status: ActionAvailability
  nestedStatus?: { entityId?: unknown; status: ActionAvailability }
  isVisible: boolean
  // These are needed to correctly group different registrations of the same `actionId` for multiple `entityId`.
  actionId: string
  entityId?: unknown
  isWorkflow: boolean
}

export type CommandDataRecord<T extends string> = Record<
  ExcludeString<T>,
  ComponentType<ActionButtonProps>
>

export type CommitmentCommandDataRecord<T extends string> = Record<
  ExcludeString<T>,
  FC<CommitmentActionButtonProps>
>

export type OverwriteCommandDataRecord<T extends AnyCommandVersion> = Partial<
  Record<ExcludeString<T['_type']>, ComponentType<ActionButtonProps>>
>

export type OverwriteCommitmentCommandDataRecord<T extends AnyCommandVersion> =
  Partial<
    Record<
      ExcludeString<T['_type']>,
      ComponentType<CommitmentActionButtonProps>
    >
  >

export type ExcludedCommandDataKeys<
  SpecificCommandData extends { _type: string },
  GeneralCommandData extends { _type: string },
> = Exclude<
  ExcludeString<SpecificCommandData['_type']>,
  ExcludeString<GeneralCommandData['_type']>
>

type AssertAllComponentsAreCommitment<T> = {
  [K in keyof T]: T[K] extends FC<infer P>
    ? P extends CommitmentActionButtonProps
      ? true
      : false
    : false
}[keyof T] extends true
  ? T
  : 'Some of the commands are not using CommitmentActionButtonProps'

/** Helper function to return an "error" type if any of the components are not using `CommitmentActionButtonProps`. */
export const ensureAllComponentsAreCommitment = <T>(
  components: T,
): AssertAllComponentsAreCommitment<T> =>
  components as AssertAllComponentsAreCommitment<T>
